/**
 * BuchhaltungEinOrtPage — EIN Ort für die Buchhaltung (Feature buchhaltung-ein-ort)
 *
 * Konsolidiert die komplette Buchhaltung an EINER Stelle — wie in einem echten
 * Buchhaltungstool. Aufbau in zwei Tab-Ebenen:
 *
 *   OBERE EBENE  — Betrieb / Firma:  „SV Büro“ · „LCS“ · „Alle“
 *   UNTERE EBENE — Bereich je Betrieb:
 *     1. „Eingangsrechnungen“  — eingangsbelege (Hook useEingangsbelege(firma)),
 *                                Beleg-Liste mit Status + Übermittlung.
 *     2. „Ausgangsrechnungen“  — bestehende Rechnungs-Liste aus BuchhaltungPage,
 *                                WIEDERVERWENDET via <BuchhaltungPage embedded
 *                                lockBusinessUnit=… /> (LCS = 'mietwagen',
 *                                SV Büro = 'gutachten'). Für SV zusätzlich eine
 *                                schlanke WinValue-Rechnungen-Liste
 *                                (Tabelle winvalue_rechnungen).
 *     3. „Dokumente“           — wichtige inbox_items, die NICHT als Beleg gelten.
 *     4. „Buchhaltung“         — monatliche Übermittlung an den Steuerberater
 *                                (eingangsbelege gruppiert nach Zeitraum), auf die
 *                                aktive Firma gefiltert.
 *
 * Maximale Wiederverwendung bestehender Hooks/Komponenten — KEINE Logik-Dopplung.
 * Die RLS („Inhaber-only“, app_is_inhaber()) bleibt unangetastet.
 *
 * Betriebs-/Firmen-Mapping (siehe lib/betriebe.ts + database.types FirmaId):
 *   SV Büro = Betrieb 'gutachten'  → eingangsbelege.firma 'gutachter_koop'
 *   LCS     = Betrieb 'mietwagen'  → eingangsbelege.firma 'lcs_gmbh'
 *
 * WIEDERVERWENDUNG (Feature finanzen-cockpit):
 *   Die vier firmen-gefilterten Tab-Inhalts-Komponenten (EingangsrechnungenTab,
 *   AusgangsrechnungenTab, DokumenteTab, BuchhaltungMonatTab) sowie der
 *   BetriebConfig-Typ und die Tab-Liste werden EXPORTIERT, damit das
 *   Finanzen-Cockpit (FinanzenCockpitPage) dieselbe Logik ohne Dopplung nutzen
 *   kann. Das Verhalten dieser Seite bleibt unverändert.
 *
 * FINANZEN-COCKPIT-V2 (Feature finanzen-cockpit-v2):
 *   - EingangsrechnungenTab: Monatsgruppierung, direkter Upload (Bucket 'inbox')
 *     und schnelles Zuordnen je Beleg (Firma, Kategorie, Monat, wiederkehrend).
 *   - BuchhaltungMonatTab: „Monat an Steuerberater senden“-Dialog mit Übersicht +
 *     Freigabe (setzt uebermittelt + erzeugt E-Mail-Entwurf) + Fristen-Banner.
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useEingangsbelege, updateEingangsbeleg, createEingangsbeleg } from '@/hooks/useBuchhaltung';
import { useInboxItems, uploadInboxItem } from '@/hooks/useInboxItems';
import { schlageKontoVor, lerneLernregel, normalisiereLieferant, type KontoVorschlag } from '@/lib/belegKonto';
import { getSignedInboxUrl } from '@/lib/inbox-items';
import { localIsoDate } from '@/lib/date-utils';
import { supabase, type BusinessUnit } from '@/lib/supabase';
import BuchhaltungPage from './BuchhaltungPage';
import type {
  Eingangsbeleg,
  FirmaId,
  UebermittlungStatus,
  InboxItem,
  InboxKlassifizierung,
} from '@/lib/database.types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Receipt, CalendarRange, FileText, Loader2, Building2, Send, Clock,
  CheckCircle2, Inbox, AlertCircle, Eye, Banknote, ArrowDownToLine, ArrowUpFromLine,
  Upload, Repeat, X, Mail, ScanLine,
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================================
// LOKALE TYP-ERWEITERUNG (Feature finanzen-cockpit-v2)
// Die DB-Spalten wiederkehrend / wiederkehrend_intervall existieren live
// (Migration 20260627_eingangsbelege_wiederkehrend.sql), sind aber bewusst
// NICHT in der zentralen database.types.ts gepflegt, um diese große Datei nicht
// zu verändern. Lokale optionale Erweiterung hält den Tab typsicher.
// ============================================================

type WiederkehrendIntervall = 'monatlich' | 'quartal' | 'jaehrlich';

interface EingangsbelegX extends Eingangsbeleg {
  wiederkehrend?: boolean | null;
  wiederkehrend_intervall?: WiederkehrendIntervall | null;
}

// ============================================================
// BETRIEBS-KONFIG
// ============================================================

export interface BetriebConfig {
  key: 'sv' | 'lcs' | 'alle';
  label: string;
  /** eingangsbelege.firma — undefined = alle Firmen */
  firma?: FirmaId;
  /** rechnungen.business_unit — undefined = alle Betriebe */
  businessUnit?: BusinessUnit;
  /** SV Büro: zusätzlich WinValue-Rechnungen anzeigen */
  withWinValue: boolean;
}

export const BETRIEBE_TABS: BetriebConfig[] = [
  { key: 'sv', label: 'SV Büro', firma: 'gutachter_koop', businessUnit: 'gutachten', withWinValue: true },
  { key: 'lcs', label: 'LCS', firma: 'lcs_gmbh', businessUnit: 'mietwagen', withWinValue: false },
  { key: 'alle', label: 'Alle', firma: undefined, businessUnit: undefined, withWinValue: false },
];

/**
 * Liefert die Betriebs-Config (Firma-Filter + Business-Unit) für eine Cockpit-Firma.
 * Wird vom Finanzen-Cockpit genutzt, um die exportierten Tab-Komponenten
 * firmen-gefiltert wiederzuverwenden. Fällt auf „SV Büro“ zurück.
 */
export function getBetriebConfig(key: 'sv' | 'lcs' | 'alle'): BetriebConfig {
  return BETRIEBE_TABS.find(b => b.key === key) || BETRIEBE_TABS[0];
}

// ============================================================
// HELPERS
// ============================================================

function formatEuro(n: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const FIRMA_LABELS: Record<string, string> = {
  gutachter_koop: 'Gutachter Koop',
  lcs_gmbh: 'LCS GmbH',
};

/** Firmen-Optionen für die schnelle Zuordnung (entsprechen FirmaId). */
const FIRMA_OPTIONS: { value: FirmaId; label: string }[] = [
  { value: 'gutachter_koop', label: 'SV Büro (Gutachter Koop)' },
  { value: 'lcs_gmbh', label: 'LCS GmbH' },
];

/** Häufige Beleg-Kategorien für die schnelle Zuordnung. */
const KATEGORIE_OPTIONS: string[] = [
  'Bürobedarf',
  'Miete',
  'Versicherung',
  'KFZ-Kosten',
  'Telekommunikation',
  'IT / Software',
  'Werbung / Marketing',
  'Reisekosten',
  'Beratung',
  'Sonstiges',
];

const INTERVALL_OPTIONS: { value: WiederkehrendIntervall; label: string }[] = [
  { value: 'monatlich', label: 'Monatlich' },
  { value: 'quartal', label: 'Quartalsweise' },
  { value: 'jaehrlich', label: 'Jährlich' },
];

// ============================================================
// ZAHLWEG & AUSLAGEN (Feature beleg-stapel-auslagen)
// ============================================================

type Zahlweg = 'bank' | 'bar' | 'privat' | 'auslage_mitarbeiter' | 'unbekannt';

const ZAHLWEG_OPTIONS: { value: Zahlweg; label: string }[] = [
  { value: 'bank', label: 'Bank' },
  { value: 'bar', label: 'Bar' },
  { value: 'privat', label: 'Privat bezahlt' },
  { value: 'auslage_mitarbeiter', label: 'Mitarbeiter-Auslage' },
  { value: 'unbekannt', label: 'Unbekannt' },
];

// ============================================================
// VORKONTIEREN + BUCHEN (Feature beleg-stapel-auslagen)
// ============================================================

/** Ein Aufwandskonto (buchungskonten, typ='aufwand') für das Vorkontieren. */
interface AufwandskontoOption {
  id: string;
  nummer: string | null;
  bezeichnung: string | null;
  sortierung: number | null;
}

/**
 * Mappt eingangsbelege.firma auf die in den Buchungstabellen verwendeten
 * Firmen-Schlüssel (buchungskonten/buchungen/buchungs_lernregeln):
 *   SV Büro  → 'sv_buero_koop'
 *   LCS      → 'lcs'
 * Akzeptiert sowohl die Live-Werte ('sv_buero_koop') als auch die in den
 * TS-Typen geführten Legacy-Labels ('gutachter_koop' / 'lcs_gmbh').
 * 'unklar' (oder Unbekanntes) ist NICHT registriert → null.
 */
function buchungsFirmaKey(firma: string | null | undefined): 'sv_buero_koop' | 'lcs' | null {
  switch (firma) {
    case 'sv_buero_koop':
    case 'gutachter_koop':
      return 'sv_buero_koop';
    case 'lcs':
    case 'lcs_gmbh':
      return 'lcs';
    default:
      return null;
  }
}

/** Gegenkonto je Zahlweg + Firma (SKR-Konten bzw. sprechende Gegenkonten). */
function gegenkontoFuer(zahlweg: Zahlweg, firmaKey: 'sv_buero_koop' | 'lcs'): string {
  switch (zahlweg) {
    case 'bank':
      return firmaKey === 'sv_buero_koop' ? '1200' : '1800';
    case 'bar':
      return firmaKey === 'sv_buero_koop' ? '1000' : '1600';
    case 'privat':
      return 'Privateinlage';
    case 'auslage_mitarbeiter':
      return 'Verb. Mitarbeiter';
    default:
      return '';
  }
}

/** Lädt die Aufwandskonten einer Firma (typ='aufwand'), sortiert. Robust. */
async function ladeAufwandskonten(firmaKey: 'sv_buero_koop' | 'lcs'): Promise<AufwandskontoOption[]> {
  try {
    const { data, error } = await supabase
      .from('buchungskonten')
      .select('id, nummer, bezeichnung, sortierung')
      .eq('firma', firmaKey)
      .eq('typ', 'aufwand')
      .order('sortierung', { ascending: true });
    if (error) throw error;
    return (data as AufwandskontoOption[]) || [];
  } catch {
    return [];
  }
}

/**
 * Bester Buchungskonto-Vorschlag aus buchungs_lernregeln: Regeln der Firma laden,
 * deren lieferant_muster im Beleg-Text (absender_firma/verwendungszweck) vorkommt
 * und die ein buchungskonto_id gesetzt haben — höchste treffer-Zahl gewinnt.
 * Gibt die buchungskonto_id zurück oder null. Robust (try/catch).
 */
async function besteKontoRegel(
  firmaKey: 'sv_buero_koop' | 'lcs',
  text: string | null | undefined,
): Promise<string | null> {
  const muster = normalisiereLieferant(text);
  if (!muster) return null;
  try {
    const { data, error } = await supabase
      .from('buchungs_lernregeln')
      .select('lieferant_muster, buchungskonto_id, treffer')
      .eq('firma', firmaKey)
      .not('buchungskonto_id', 'is', null)
      .order('treffer', { ascending: false })
      .limit(200);
    if (error) throw error;
    const regeln = (data as { lieferant_muster: string; buchungskonto_id: string | null; treffer: number | null }[]) || [];
    let beste: { buchungskonto_id: string | null; treffer: number | null } | null = null;
    for (const r of regeln) {
      const rm = normalisiereLieferant(r.lieferant_muster);
      if (!rm || !r.buchungskonto_id) continue;
      const passt = muster === rm || muster.includes(rm) || rm.includes(muster);
      if (!passt) continue;
      if (!beste || (r.treffer || 0) > (beste.treffer || 0)) beste = r;
    }
    return beste?.buchungskonto_id ?? null;
  } catch {
    return null;
  }
}

/** Verstärkt/legt eine Buchungskonto-Lernregel an (firma, lieferant_muster,
 *  buchungskonto_id). Wirft NICHT — Lernen darf das Buchen nie blockieren. */
async function lerneKontoRegel(
  firmaKey: 'sv_buero_koop' | 'lcs',
  text: string | null | undefined,
  buchungskontoId: string,
): Promise<void> {
  const muster = normalisiereLieferant(text);
  if (!muster || !buchungskontoId) return;
  try {
    let treffer = 1;
    try {
      const { data } = await supabase
        .from('buchungs_lernregeln')
        .select('treffer')
        .eq('firma', firmaKey)
        .eq('lieferant_muster', muster)
        .maybeSingle();
      const vorhanden = (data as { treffer: number | null } | null)?.treffer;
      if (typeof vorhanden === 'number' && Number.isFinite(vorhanden)) treffer = vorhanden + 1;
    } catch {
      // Default treffer = 1
    }
    const row: Record<string, unknown> = {
      firma: firmaKey,
      lieferant_muster: muster,
      buchungskonto_id: buchungskontoId,
      typ: 'ausgabe',
      treffer,
      zuletzt_genutzt: new Date().toISOString(),
    };
    const { error } = await supabase
      .from('buchungs_lernregeln')
      .upsert(row as any, { onConflict: 'firma,lieferant_muster' });
    if (error) throw error;
  } catch {
    // robust ignorieren
  }
}

/** Übermittlungs-Zeitraum (YYYY-MM) eines Belegs herleiten:
 *  bevorzugt uebermittlung_zeitraum, sonst aus Belegdatum/Eingangsdatum. */
function belegZeitraum(b: Eingangsbeleg): string {
  if (b.uebermittlung_zeitraum) return b.uebermittlung_zeitraum;
  const basis = b.belegdatum || b.eingangsdatum || b.created_at;
  if (!basis) return 'ohne-datum';
  return basis.slice(0, 7); // YYYY-MM
}

function monatLabel(zeitraum: string): string {
  if (zeitraum === 'ohne-datum') return 'Ohne Datum';
  const [jahr, monat] = zeitraum.split('-');
  const d = new Date(Number(jahr), Number(monat) - 1, 1);
  if (Number.isNaN(d.getTime())) return zeitraum;
  return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
}

/** Liste der letzten 12 Monate als Zeitraum-Optionen (YYYY-MM). */
function letzteMonate(anzahl = 12): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < anzahl; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ value: ym, label: monatLabel(ym) });
  }
  return out;
}

/** app_settings-Werte (TEXT key/value) als Map lesen — robust gegen fehlende Tabelle/Keys. */
async function ladeAppSettings(keys: string[]): Promise<Record<string, string>> {
  try {
    const { data, error } = await supabase
      .from('app_settings')
      .select('key, value')
      .in('key', keys);
    if (error) return {};
    const map: Record<string, string> = {};
    for (const row of (data as { key: string; value: string }[]) || []) {
      if (row?.key) map[row.key] = row.value;
    }
    return map;
  } catch {
    return {};
  }
}

interface FinanzKategorieOption {
  id: string;
  name: string;
  typ: string;
}

/** Lädt finanz_kategorien (robust gegen fehlende Tabelle) — für den
 *  lernenden Buchungskonto-Vorschlag im Eingangsbeleg. */
async function ladeFinanzKategorien(): Promise<FinanzKategorieOption[]> {
  try {
    const { data, error } = await supabase
      .from('finanz_kategorien')
      .select('id, name, typ')
      .order('name', { ascending: true });
    if (error) throw error;
    return (data as FinanzKategorieOption[]) || [];
  } catch {
    return [];
  }
}

const DEFAULT_STB_EMAIL = 'N.Krutmann@stb-hammerschmidt.de';
const DEFAULT_FRIST_TAG = 10;

function KpiBox({ label, value, icon: Icon, color }: {
  label: string; value: string | number; icon: typeof Receipt; color: string;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${color}12` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="text-xl font-bold text-gray-900 tracking-tight">{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

function UebermittlungBadge({ status, gebucht }: { status: UebermittlungStatus; gebucht: boolean }) {
  if (gebucht) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-600">
        <CheckCircle2 className="w-3 h-3" /> Gebucht
      </span>
    );
  }
  const cfg: Record<UebermittlungStatus, { label: string; color: string }> = {
    offen: { label: 'Offen', color: 'bg-blue-50 text-blue-600' },
    spaeter: { label: 'Später', color: 'bg-amber-50 text-amber-600' },
    uebermittelt: { label: 'Übermittelt', color: 'bg-emerald-50 text-emerald-600' },
  };
  const c = cfg[status] || cfg.offen;
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.color}`}>{c.label}</span>;
}

function LoadingBox() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
    </div>
  );
}

function EmptyBox({ icon: Icon, text }: { icon: typeof Inbox; text: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl text-center py-12 text-gray-400">
      <Icon className="w-8 h-8 mx-auto mb-2 opacity-50" />
      <div className="text-sm">{text}</div>
    </div>
  );
}

// ============================================================
// TAB: EINGANGSRECHNUNGEN (eingangsbelege gefiltert nach Firma)
// Monatsgruppierung + Direkt-Upload + schnelles Zuordnen
// ============================================================


// ============================================================
// PANEL: Rechnungen aus E-Mail (E-Mail-Intake)
// Aufklappbares Panel — listet Gmail-Nachrichten mit PDF-Anhang und
// uebernimmt einzelne als Eingangsbeleg (Edge Function 'email-rechnungen').
// ============================================================

interface EmailRechnungItem {
  messageId: string;
  from: string;
  subject: string;
  date: string;
  attachmentId: string;
  filename: string;
  istRechnung?: boolean;
}

// userId analog zu useGmail: pro Browser generierte ID in localStorage.
// Die Edge Function faellt serverseitig auf den geteilten Admin-Token zurueck.
function getGmailUserId(): string {
  try {
    let userId = localStorage.getItem('gmail_user_id');
    if (!userId) {
      userId = `user_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      localStorage.setItem('gmail_user_id', userId);
    }
    return userId;
  } catch {
    return '';
  }
}

function EmailRechnungenPanel({ firma, onImported }: { firma?: FirmaId; onImported: () => void }) {
  const [open, setOpen] = useState(false);
  const [laden, setLaden] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [items, setItems] = useState<EmailRechnungItem[] | null>(null);
  const [importiere, setImportiere] = useState<string | null>(null);

  const pruefeEmails = useCallback(async () => {
    setLaden(true);
    setFehler(null);
    try {
      const { data, error } = await supabase.functions.invoke('email-rechnungen', {
        body: { action: 'list', userId: getGmailUserId() },
      });
      if (error) {
        setFehler(error.message || 'Abruf fehlgeschlagen');
        setItems(null);
      } else if (data && (data as any).error) {
        setFehler((data as any).error);
        setItems(null);
      } else {
        setItems(Array.isArray(data) ? (data as EmailRechnungItem[]) : []);
      }
    } catch (err: any) {
      setFehler(err.message || 'Abruf fehlgeschlagen');
      setItems(null);
    } finally {
      setLaden(false);
    }
  }, []);

  const uebernehmen = useCallback(async (item: EmailRechnungItem) => {
    setImportiere(item.messageId);
    try {
      const { data, error } = await supabase.functions.invoke('email-rechnungen', {
        body: {
          action: 'import',
          userId: getGmailUserId(),
          messageId: item.messageId,
          attachmentId: item.attachmentId,
          filename: item.filename,
          firma,
        },
      });
      if (error) {
        toast.error('Uebernahme fehlgeschlagen: ' + (error.message || 'Unbekannt'));
      } else if (data && (data as any).error) {
        toast.error('Uebernahme fehlgeschlagen: ' + (data as any).error);
      } else {
        toast.success('Als Beleg uebernommen: ' + item.filename);
        // Uebernommene Zeile aus der Liste entfernen und Belegliste aktualisieren.
        setItems(prev => (prev ? prev.filter(x => x.messageId !== item.messageId) : prev));
        onImported();
      }
    } catch (err: any) {
      toast.error('Uebernahme fehlgeschlagen: ' + (err.message || 'Unbekannt'));
    } finally {
      setImportiere(null);
    }
  }, [firma, onImported]);

  return (
    <div className="mb-4 rounded-xl border border-gray-200 bg-white overflow-hidden" data-testid="email-intake-panel">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50/60 transition-colors"
        data-testid="email-intake-toggle"
      >
        <span className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#C41E3A]" />
          <span className="text-sm font-semibold text-gray-800">Rechnungen aus E-Mail</span>
        </span>
        <span className="text-[11px] text-gray-400">{open ? 'Zuklappen' : 'Aufklappen'}</span>
      </button>

      {open && (
        <div className="border-t border-gray-100 px-4 py-3">
          <div className="flex items-center gap-3 mb-3">
            <button
              type="button"
              onClick={pruefeEmails}
              disabled={laden}
              className="inline-flex items-center gap-2 rounded-lg bg-[#C41E3A] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#a81830] disabled:opacity-60 disabled:cursor-wait"
              data-testid="email-intake-check"
            >
              {laden ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {laden ? 'Prueft…' : 'E-Mails pruefen'}
            </button>
            <span className="text-[11px] text-gray-400">
              Letzte Gmail-Nachrichten mit PDF-Anhang
            </span>
          </div>

          {fehler && (
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800" data-testid="email-intake-error">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{fehler}</span>
            </div>
          )}

          {items && items.length === 0 && !fehler && (
            <div className="text-[13px] text-gray-400 py-2">
              Keine passenden E-Mails gefunden.
            </div>
          )}

          {items && items.length > 0 && (
            <div className="divide-y divide-gray-50 -mx-1">
              {items.map(item => (
                <div key={item.messageId} className="flex items-center justify-between gap-3 px-1 py-2.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900 truncate">
                        {item.subject || '(ohne Betreff)'}
                      </span>
                      {item.istRechnung && (
                        <span className="flex-shrink-0 rounded bg-[#C41E3A]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#C41E3A]">
                          Rechnung
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {item.from} · {item.date}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-400">
                      <FileText className="w-3 h-3" />
                      <span className="truncate">{item.filename}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => uebernehmen(item)}
                    disabled={importiere === item.messageId}
                    className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[13px] font-semibold text-gray-700 hover:border-[#C41E3A] hover:text-[#C41E3A] disabled:opacity-60 disabled:cursor-wait"
                    data-testid="email-intake-import"
                  >
                    {importiere === item.messageId
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <ArrowDownToLine className="w-3.5 h-3.5" />}
                    Als Beleg uebernehmen
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function EingangsrechnungenTab({ firma }: { firma?: FirmaId }) {
  const { data: belegeRaw, loading, refetch } = useEingangsbelege(firma);
  const belege = (belegeRaw || []) as EingangsbelegX[];
  const [uploading, setUploading] = useState(false);
  // Fortschritt des automatischen Auslesens (OCR) beim Stapel-Upload:
  // null = inaktiv, sonst { aktuell, gesamt } für „Lese aus: X von N…".
  const [ocrFortschritt, setOcrFortschritt] = useState<{ aktuell: number; gesamt: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const kpis = useMemo(() => {
    const anzahl = belege.length;
    const summe = belege.reduce((s, b) => s + (b.brutto_betrag || 0), 0);
    const offen = belege.filter(b => (b.uebermittlung_status ?? 'offen') === 'offen').length;
    const uebermittelt = belege.filter(b => b.uebermittlung_status === 'uebermittelt').length;
    return { anzahl, summe, offen, uebermittelt };
  }, [belege]);

  // Monatsgruppierung (nach abgeleitetem Zeitraum YYYY-MM) — absteigend.
  const gruppen = useMemo(() => {
    const byMonat = new Map<string, EingangsbelegX[]>();
    for (const b of belege) {
      const z = belegZeitraum(b);
      if (!byMonat.has(z)) byMonat.set(z, []);
      byMonat.get(z)!.push(b);
    }
    const monate = Array.from(byMonat.keys()).sort((a, c) => {
      if (a === 'ohne-datum') return 1;
      if (c === 'ohne-datum') return -1;
      return c.localeCompare(a);
    });
    return monate.map(z => {
      const items = byMonat.get(z)!;
      return {
        zeitraum: z,
        items,
        summe: items.reduce((s, it) => s + (it.brutto_betrag || 0), 0),
      };
    });
  }, [belege]);

  // Firma, der neue Belege zugeordnet werden. Im „Alle“-Tab (firma undefined)
  // greift ein sinnvoller Default (SV Büro / gutachter_koop).
  const zielFirma: FirmaId = firma || 'gutachter_koop';

  // Pro Datei: Datei in den privaten Bucket 'inbox' hochladen (uploadInboxItem)
  // und direkt einen eingangsbelege-Datensatz anlegen (status 'neu'). So
  // entsteht sofort ein revisionssicherer Beleg (GoBD), unabhängig vom
  // Eingangskorb-Workflow.
  const verarbeiteDateien = useCallback(async (fileList: FileList | File[]) => {
    const files = Array.from(fileList).filter(Boolean);
    if (files.length === 0) return;
    setUploading(true);
    let ok = 0;
    let fehler = 0;
    for (const file of files) {
      try {
        let inboxItemId: string | null = null;
        try {
          const item = await uploadInboxItem(file);
          inboxItemId = (item as any)?.id ?? null;
        } catch (uErr: any) {
          // Upload in den Bucket fehlgeschlagen — Beleg NICHT ohne Datei anlegen.
          console.error('Inbox-Upload fehlgeschlagen:', uErr);
          fehler++;
          continue;
        }
        const angelegt = await createEingangsbeleg({
          firma: zielFirma,
          status: 'neu',
          quelle_datei: file.name,
          eingangsdatum: localIsoDate(new Date()),
          ...(inboxItemId ? { inbox_item_id: inboxItemId } : {}),
        } as any);
        ok++;

        // AUTO-OCR (Feature beleg-stapel-auslagen): direkt nach dem Anlegen den
        // Beleg automatisch auslesen lassen. Sequenziell, in try/catch — ein
        // Fehler eines einzelnen Belegs darf den Stapel NICHT stoppen.
        const belegId = (angelegt as any)?.id;
        if (belegId) {
          setOcrFortschritt({ aktuell: ok, gesamt: files.length });
          try {
            await supabase.functions.invoke('beleg-ocr', { body: { beleg_id: belegId } });
          } catch (ocrErr: any) {
            // OCR ist optional — niemals den Stapel blockieren.
            console.error('Auto-OCR fehlgeschlagen:', ocrErr);
          }
        }
      } catch (err: any) {
        console.error('Beleg anlegen fehlgeschlagen:', err);
        fehler++;
      }
    }
    setUploading(false);
    setOcrFortschritt(null);
    if (ok > 0) {
      toast.success(`${ok} ${ok === 1 ? 'Beleg' : 'Belege'} hochgeladen und ausgelesen`);
    }
    if (fehler > 0) {
      toast.error(`${fehler} Datei(en) konnten nicht hochgeladen werden`);
    }
    if (ok > 0) refetch();
  }, [zielFirma, refetch]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) await verarbeiteDateien(files);
    e.target.value = '';
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) await verarbeiteDateien(files);
  };

  if (loading) return <LoadingBox />;

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <KpiBox label="Belege gesamt" value={kpis.anzahl} icon={Receipt} color="#2563EB" />
        <KpiBox label="Summe (brutto)" value={formatEuro(kpis.summe)} icon={Banknote} color="#0D9488" />
        <KpiBox label="Offen (StB)" value={kpis.offen} icon={Clock} color="#D97706" />
        <KpiBox label="Offen (StB)" value={kpis.offen} icon={Clock} color="#D97706" />
        <KpiBox label="Übermittelt" value={kpis.uebermittelt} icon={Send} color="#059669" />
      </div>

      {/* Panel: Rechnungen aus E-Mail (E-Mail-Intake) */}
      <EmailRechnungenPanel firma={firma} onImported={refetch} />

      {/* Drag&Drop-Beleg-Upload */}
      <div
        onClick={() => { if (!uploading) fileInputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        data-testid="eingang-dropzone"
        className={`mb-4 flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed px-4 py-6 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-[#C41E3A] bg-[#C41E3A]/5' : 'border-gray-200 bg-gray-50/60 hover:border-gray-300'
        } ${uploading ? 'opacity-60 cursor-wait' : ''}`}
      >
        {uploading ? (
          <Loader2 className="w-6 h-6 text-[#C41E3A] animate-spin" />
        ) : (
          <Upload className="w-6 h-6 text-gray-400" />
        )}
        <div className="text-sm font-semibold text-gray-700">
          {uploading
            ? (ocrFortschritt
                ? `Lese aus: ${ocrFortschritt.aktuell} von ${ocrFortschritt.gesamt}…`
                : 'Lädt hoch…')
            : 'Belege hier ablegen oder klicken'}
        </div>
        <div className="text-[11px] text-gray-400">
          PDF oder Bild · mehrere Dateien möglich · es wird sofort ein Beleg angelegt
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          onChange={handleUpload}
          disabled={uploading}
          className="hidden"
          data-testid="eingang-upload-input"
        />
      </div>

      {belege.length === 0 ? (
        <EmptyBox icon={Inbox} text="Keine Eingangsrechnungen vorhanden" />
      ) : (
        <div className="space-y-6">
          {gruppen.map(g => (
            <div key={g.zeitraum} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/60">
                <div className="flex items-center gap-2">
                  <CalendarRange className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-bold text-gray-900">{monatLabel(g.zeitraum)}</span>
                </div>
                <span className="text-[11px] text-gray-500">
                  {g.items.length} Belege · {formatEuro(g.summe)}
                </span>
              </div>

              <div className="divide-y divide-gray-50">
                {g.items.map(b => (
                  <BelegZuordnenRow key={b.id} beleg={b} showFirma={!firma} onSaved={refetch} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Eine Beleg-Zeile mit Inline-Schnellzuordnung (Firma, Kategorie, Monat, wiederkehrend). */
function BelegZuordnenRow({ beleg, showFirma, onSaved }: { beleg: EingangsbelegX; showFirma: boolean; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [firma, setFirma] = useState<FirmaId>(beleg.firma);
  const [kategorie, setKategorie] = useState<string>(beleg.kategorie || '');
  const [monat, setMonat] = useState<string>(belegZeitraum(beleg));
  const [wiederkehrend, setWiederkehrend] = useState<boolean>(beleg.wiederkehrend === true);
  const [intervall, setIntervall] = useState<WiederkehrendIntervall>(
    (beleg.wiederkehrend_intervall as WiederkehrendIntervall) || 'monatlich',
  );

  // Zahlweg & Auslagen (Feature beleg-stapel-auslagen).
  const [zahlweg, setZahlweg] = useState<Zahlweg>(((beleg as any).zahlweg as Zahlweg) || 'unbekannt');
  const [verauslagtVon, setVerauslagtVon] = useState<string>((beleg as any).verauslagt_von || '');
  const istAuslage = zahlweg === 'privat' || zahlweg === 'auslage_mitarbeiter';

  // Vorkontieren + Buchen (Feature beleg-stapel-auslagen).
  const firmaKey = buchungsFirmaKey(beleg.firma);
  const [aufwandskonten, setAufwandskonten] = useState<AufwandskontoOption[]>([]);
  const [aufwandskontoId, setAufwandskontoId] = useState<string>('');
  const [buchenBusy, setBuchenBusy] = useState(false);

  // Lernender Buchungskonto-/Kategorie-Vorschlag (Feature beleg-pipeline-lernend).
  const [finanzKategorien, setFinanzKategorien] = useState<FinanzKategorieOption[]>([]);
  const [kategorieId, setKategorieId] = useState<string>((beleg as any).kategorie_id || '');
  const [vorschlag, setVorschlag] = useState<KontoVorschlag | null>(null);

  // Freitext-Beschreibung des Lieferanten für Suche + Lernen.
  const lieferantText = useMemo(
    () => beleg.absender_firma || beleg.absender_name || beleg.verwendungszweck || '',
    [beleg.absender_firma, beleg.absender_name, beleg.verwendungszweck],
  );

  // Beim Aufklappen: finanz_kategorien laden und — falls noch keine kategorie_id
  // gesetzt ist — einen lernenden Vorschlag vorbelegen.
  useEffect(() => {
    if (!open) return;
    let aktiv = true;
    (async () => {
      const kats = await ladeFinanzKategorien();
      if (!aktiv) return;
      setFinanzKategorien(kats);
      try {
        const v = await schlageKontoVor(beleg.firma, lieferantText);
        if (!aktiv) return;
        setVorschlag(v);
        // Nur vorbelegen, wenn der Nutzer noch nichts gewählt hat.
        if (!kategorieId && v.kategorie_id) setKategorieId(v.kategorie_id);
      } catch {
        // Vorschlag ist optional — niemals blockieren.
      }
    })();
    return () => { aktiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Beim Aufklappen: Aufwandskonten der Firma laden (nur wenn registriert,
  // NICHT 'unklar') und mit dem besten Lernregel-Match vorbelegen, sonst mit
  // dem ersten Aufwandskonto. Robust — niemals blockieren.
  useEffect(() => {
    if (!open || !firmaKey) return;
    let aktiv = true;
    (async () => {
      const konten = await ladeAufwandskonten(firmaKey);
      if (!aktiv) return;
      setAufwandskonten(konten);
      if (konten.length === 0) return;
      try {
        const beste = await besteKontoRegel(firmaKey, lieferantText);
        if (!aktiv) return;
        const treffer = beste && konten.some(k => k.id === beste) ? beste : null;
        setAufwandskontoId(prev => prev || treffer || konten[0].id);
      } catch {
        setAufwandskontoId(prev => prev || konten[0].id);
      }
    })();
    return () => { aktiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, firmaKey]);

  const monatsOptionen = useMemo(() => {
    const opts = letzteMonate(12);
    // Aktuellen Zeitraum sicher als Option enthalten.
    if (!opts.some(o => o.value === monat) && monat !== 'ohne-datum') {
      opts.unshift({ value: monat, label: monatLabel(monat) });
    }
    return opts;
  }, [monat]);

  // OCR-Auslesen (Feature beleg-ocr): ruft die Edge Function 'beleg-ocr' auf, die
  // Lieferant/Datum/Beträge per Google Vision als Vorschlag in eingangsbelege schreibt.
  // Bei Erfolg wird die Liste neu geladen (onSaved -> refetch).
  const handleOcr = async () => {
    setOcrBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('beleg-ocr', {
        body: { beleg_id: beleg.id },
      });
      if (error) {
        toast.error('Auslesen fehlgeschlagen: ' + (error.message || 'Unbekannt'));
        return;
      }
      if (data && (data as any).error) {
        toast.error('Auslesen fehlgeschlagen: ' + (data as any).error);
        return;
      }
      const lieferant = (data as any)?.lieferant ?? '—';
      const brutto = (data as any)?.brutto;
      const bruttoText = typeof brutto === 'number' ? formatEuro(brutto) : (brutto ?? '—');
      toast.success('Beleg ausgelesen: ' + lieferant + ' · ' + bruttoText + ' €');
      onSaved();
    } catch (err: any) {
      toast.error('Auslesen fehlgeschlagen: ' + (err?.message || 'Unbekannt'));
    } finally {
      setOcrBusy(false);
    }
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      // „Verauslagt von": bei privat sinnvoller Default, sonst der eingegebene Wert.
      const verauslagtFinal = istAuslage
        ? (verauslagtVon.trim() || (zahlweg === 'privat' ? 'inhaber_privat' : ''))
        : null;

      const updates: Record<string, unknown> = {
        firma,
        kategorie: kategorie || null,
        kategorie_id: kategorieId || null,
        uebermittlung_zeitraum: monat,
        wiederkehrend,
        wiederkehrend_intervall: wiederkehrend ? intervall : null,
        // Zahlweg & Auslagen (Feature beleg-stapel-auslagen).
        zahlweg,
        verauslagt_von: verauslagtFinal,
        // Bei Auslagen offene Erstattung in Höhe des Bruttobetrags vormerken.
        erstattung_status: istAuslage ? 'offen' : null,
        erstattung_betrag: istAuslage ? (beleg.brutto_betrag || 0) : null,
      };
      await updateEingangsbeleg(beleg.id, updates as any);

      // Lernen: bei gesetzter Kategorie eine Lernregel verstärken/anlegen, damit
      // gleiche Lieferanten künftig sicherer vorgeschlagen werden. Wirft nicht.
      if (kategorieId) {
        await lerneLernregel({
          firma: beleg.firma,
          text: lieferantText,
          kategorie_id: kategorieId,
          konto_skr: vorschlag?.konto_skr ?? null,
          typ: 'ausgabe',
        });
      }

      toast.success('Zuordnung gespeichert');
      setOpen(false);
      onSaved();
    } catch (err: any) {
      toast.error('Fehler: ' + (err.message || 'Unbekannt'));
    } finally {
      setBusy(false);
    }
  };

  // Was fehlt für eine 100%-sichere Buchung (GoBD)? Leeres Array = buchbar.
  const buchenFehlt = useMemo<string[]>(() => {
    const fehlt: string[] = [];
    if (!firmaKey) fehlt.push('Firma muss SV Büro oder LCS sein');
    if (!(beleg.brutto_betrag && beleg.brutto_betrag !== 0)) fehlt.push('Bruttobetrag');
    if (!aufwandskontoId) fehlt.push('Aufwandskonto');
    if (zahlweg === 'unbekannt') fehlt.push('Zahlweg');
    return fehlt;
  }, [firmaKey, beleg.brutto_betrag, aufwandskontoId, zahlweg]);

  const buchbar = buchenFehlt.length === 0;

  // Buchen: erzeugt eine Buchung in 'buchungen' (Ausgabe = negativ) und
  // verstärkt die Buchungskonto-Lernregel. Nur möglich, wenn buchbar.
  const handleBuchen = async () => {
    if (!buchbar || !firmaKey) return;
    setBuchenBusy(true);
    try {
      const brutto = beleg.brutto_betrag || 0;
      const netto = (beleg.netto_betrag != null ? beleg.netto_betrag : brutto / 1.19);
      const steuer = brutto - netto;
      const gewaehlt = aufwandskonten.find(k => k.id === aufwandskontoId) || null;

      const buchung: Record<string, unknown> = {
        firma: firmaKey,
        buchung_datum: beleg.belegdatum || localIsoDate(new Date()),
        betrag: -1 * brutto,
        netto: -1 * netto,
        steuer,
        steuersatz: 19,
        buchungskonto_id: aufwandskontoId,
        gegenkonto: gegenkontoFuer(zahlweg, firmaKey),
        buchungstext: beleg.absender_firma || beleg.absender_name || beleg.verwendungszweck || '',
        eingangsbeleg_id: beleg.id,
        beleg_vorhanden: true,
        status: 'gebucht',
        quelle: 'beleg',
      };

      const { error } = await supabase.from('buchungen').insert(buchung as any);
      if (error) {
        toast.error('Buchen fehlgeschlagen: ' + (error.message || 'Unbekannt'));
        return;
      }

      // Lernregel verstärken (robust, blockiert nie).
      await lerneKontoRegel(firmaKey, lieferantText, aufwandskontoId);

      const nummer = gewaehlt?.nummer || '';
      const bezeichnung = gewaehlt?.bezeichnung || 'Aufwandskonto';
      toast.success(`Beleg gebucht auf ${nummer} ${bezeichnung}`.trim());
      setOpen(false);
      onSaved();
    } catch (err: any) {
      toast.error('Buchen fehlgeschlagen: ' + (err?.message || 'Unbekannt'));
    } finally {
      setBuchenBusy(false);
    }
  };

  return (
    <div data-testid={`eingangsrechnung-${beleg.id}`} className="px-4 py-2.5 hover:bg-gray-50/50 transition-colors">
      <div className="flex items-center gap-3">
        <Receipt className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm text-gray-900 truncate">
            {beleg.absender_firma || beleg.absender_name || beleg.verwendungszweck || beleg.beleg_nr || '—'}
          </div>
          <div className="text-[10px] text-gray-400">
            {formatDate(beleg.belegdatum || beleg.eingangsdatum)}
            {showFirma ? ` · ${FIRMA_LABELS[beleg.firma] || beleg.firma}` : ''}
            {beleg.kategorie ? ` · ${beleg.kategorie}` : ''}
            {beleg.wiederkehrend ? ' · wiederkehrend' : ''}
          </div>
          {(beleg as any).erstattung_status === 'offen' && (
            <div className="text-[10px] font-medium text-amber-600">
              Erstattung offen: {formatEuro((beleg as any).erstattung_betrag ?? beleg.brutto_betrag ?? 0)} € an {(beleg as any).verauslagt_von || '—'}
            </div>
          )}
        </div>
        <div className="text-right whitespace-nowrap font-semibold text-gray-900 text-sm">
          {formatEuro(beleg.brutto_betrag)}
        </div>
        <UebermittlungBadge
          status={(beleg.uebermittlung_status ?? 'offen') as UebermittlungStatus}
          gebucht={beleg.gebucht_beim_stb === true}
        />
        <button
          data-testid={`beleg-ocr-${beleg.id}`}
          onClick={handleOcr}
          disabled={ocrBusy}
          title="Beleg per OCR auslesen"
          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          {ocrBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ScanLine className="w-3 h-3" />}
          Auslesen
        </button>
        <button
          data-testid={`beleg-zuordnen-${beleg.id}`}
          onClick={() => setOpen(o => !o)}
          className="px-2 py-1 text-[10px] font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
        >
          Zuordnen
        </button>
      </div>

      {open && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 bg-gray-50/70 border border-gray-100 rounded-lg p-3">
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase">Firma</label>
            <select value={firma} onChange={(e) => setFirma(e.target.value as FirmaId)} className="mt-1 w-full px-2 py-1.5 rounded border border-gray-200 bg-white text-xs">
              {FIRMA_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase">Kategorie</label>
            <select value={kategorie} onChange={(e) => setKategorie(e.target.value)} className="mt-1 w-full px-2 py-1.5 rounded border border-gray-200 bg-white text-xs">
              <option value="">— wählen —</option>
              {KATEGORIE_OPTIONS.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase">Buchungskonto</label>
            <select
              value={kategorieId}
              onChange={(e) => setKategorieId(e.target.value)}
              className="mt-1 w-full px-2 py-1.5 rounded border border-gray-200 bg-white text-xs"
              data-testid={`beleg-konto-${beleg.id}`}
            >
              <option value="">— wählen —</option>
              {finanzKategorien.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
            </select>
            {vorschlag && vorschlag.kategorie_id && vorschlag.kategorie_id === kategorieId && (
              <div className="mt-1 text-[10px] text-emerald-600">
                Vorschlag{vorschlag.quelle === 'lernregel' ? ' (gelernt)' : ''}
                {vorschlag.kategorie_name ? `: ${vorschlag.kategorie_name}` : ''}
              </div>
            )}
          </div>
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase">Monat (StB)</label>
            <select value={monat} onChange={(e) => setMonat(e.target.value)} className="mt-1 w-full px-2 py-1.5 rounded border border-gray-200 bg-white text-xs">
              {monatsOptionen.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase">Zahlweg</label>
            <select
              value={zahlweg}
              onChange={(e) => setZahlweg(e.target.value as Zahlweg)}
              className="mt-1 w-full px-2 py-1.5 rounded border border-gray-200 bg-white text-xs"
              data-testid={`beleg-zahlweg-${beleg.id}`}
            >
              {ZAHLWEG_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {istAuslage && (
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase">Verauslagt von</label>
              <input
                type="text"
                value={verauslagtVon}
                onChange={(e) => setVerauslagtVon(e.target.value)}
                placeholder={zahlweg === 'privat' ? 'inhaber_privat' : 'Name Mitarbeiter'}
                className="mt-1 w-full px-2 py-1.5 rounded border border-gray-200 bg-white text-xs"
                data-testid={`beleg-verauslagt-${beleg.id}`}
              />
              <div className="mt-1 text-[10px] text-amber-600">
                Erstattung offen: {formatEuro(beleg.brutto_betrag || 0)} an {verauslagtVon.trim() || (zahlweg === 'privat' ? 'inhaber_privat' : '—')}
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase">Wiederkehrend</label>
            <div className="mt-1 flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wiederkehrend}
                  onChange={(e) => setWiederkehrend(e.target.checked)}
                  className="rounded"
                  data-testid={`beleg-wiederkehrend-${beleg.id}`}
                />
                <Repeat className="w-3.5 h-3.5 text-gray-400" />
              </label>
              {wiederkehrend && (
                <select value={intervall} onChange={(e) => setIntervall(e.target.value as WiederkehrendIntervall)} className="flex-1 px-2 py-1.5 rounded border border-gray-200 bg-white text-xs">
                  {INTERVALL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              )}
            </div>
          </div>

          {firmaKey && (
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase">Aufwandskonto</label>
              <select
                value={aufwandskontoId}
                onChange={(e) => setAufwandskontoId(e.target.value)}
                className="mt-1 w-full px-2 py-1.5 rounded border border-gray-200 bg-white text-xs"
                data-testid={`beleg-aufwandskonto-${beleg.id}`}
              >
                <option value="">— wählen —</option>
                {aufwandskonten.map(k => (
                  <option key={k.id} value={k.id}>
                    {[k.nummer, k.bezeichnung].filter(Boolean).join(' ') || k.id}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="sm:col-span-2 lg:col-span-4 flex flex-wrap items-center justify-end gap-2">
            {!buchbar && (
              <span className="mr-auto text-[10px] text-amber-600" data-testid={`beleg-buchen-fehlt-${beleg.id}`}>
                Zum Buchen fehlt: {buchenFehlt.join(', ')}
              </span>
            )}
            <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700">
              Abbrechen
            </button>
            <button
              data-testid={`beleg-zuordnen-save-${beleg.id}`}
              onClick={handleSave}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[#C41E3A] hover:bg-[#A3172F] rounded-lg transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Speichern
            </button>
            <button
              data-testid={`beleg-buchen-${beleg.id}`}
              onClick={handleBuchen}
              disabled={buchenBusy || !buchbar}
              title={buchbar ? 'Beleg verbuchen' : `Zum Buchen fehlt: ${buchenFehlt.join(', ')}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {buchenBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Banknote className="w-3.5 h-3.5" />}
              Buchen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// TAB: AUSGANGSRECHNUNGEN
// ============================================================

interface WinValueRechnung {
  id: string;
  akte_id: string;
  dateiname: string;
  download_url: string | null;
  erstellt_am: string | null;
  akten?: { akten_nr?: string | null; kunde_name?: string | null; business_unit?: string | null } | null;
}

/** Schlanke WinValue-Rechnungs-Liste für SV Büro (Tabelle winvalue_rechnungen).
 *  RLS: authenticated darf lesen — Inhaber-only-Schutz bleibt unverändert. */
function WinValueRechnungenListe() {
  const [rows, setRows] = useState<WinValueRechnung[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('winvalue_rechnungen')
        .select('id, akte_id, dateiname, download_url, erstellt_am, akten!inner(akten_nr, kunde_name, business_unit)')
        .eq('akten.business_unit', 'gutachten')
        .order('erstellt_am', { ascending: false })
        .limit(500);
      if (err) {
        const msg = err.message || '';
        // Tabelle/View evtl. noch nicht migriert → leere Liste statt Fehler
        if (msg.includes('PGRST205') || msg.includes('does not exist') || msg.includes('schema cache')) {
          setRows([]);
        } else {
          throw err;
        }
      } else {
        setRows((data as unknown as WinValueRechnung[]) || []);
      }
    } catch (e: any) {
      setError(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingBox />;

  return (
    <div>
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4 flex items-start gap-3">
        <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-700">
          WinValue-Rechnungen (aus den Gutachten-Akten synchronisierte Rechnungsdokumente).
          Klassische Ausgangsrechnungen des SV Büros erscheinen oben in der Rechnungs-Liste.
        </div>
      </div>

      {error ? (
        <div className="flex items-center gap-2 text-sm text-red-600 py-4">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      ) : rows.length === 0 ? (
        <EmptyBox icon={FileText} text="Keine WinValue-Rechnungen vorhanden" />
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  <th className="px-4 py-3 w-8" />
                  <th className="text-left px-3 py-3 text-xs font-medium text-gray-500">Dateiname</th>
                  <th className="text-left px-3 py-3 text-xs font-medium text-gray-500">Akte</th>
                  <th className="text-left px-3 py-3 text-xs font-medium text-gray-500">Datum</th>
                  <th className="text-right px-3 py-3 text-xs font-medium text-gray-500">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} data-testid={`winvalue-${r.id}`} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-2.5 w-8"><FileText className="w-4 h-4 text-gray-400" /></td>
                    <td className="px-3 py-2.5">
                      <div className="text-gray-900 truncate max-w-[280px]">{r.dateiname}</div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-500">
                      {r.akten?.akten_nr || '—'}
                      {r.akten?.kunde_name ? ` · ${r.akten.kunde_name}` : ''}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{formatDate(r.erstellt_am)}</td>
                    <td className="px-3 py-2.5 text-right">
                      {r.download_url ? (
                        <a
                          href={r.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-[#C41E3A] bg-red-50 rounded hover:bg-red-100 transition-colors"
                        >
                          <Eye className="w-3 h-3" /> Öffnen
                        </a>
                      ) : (
                        <span className="text-[10px] text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export function AusgangsrechnungenTab({ betrieb }: { betrieb: BetriebConfig }) {
  return (
    <div className="space-y-8">
      {/* Reguläre Ausgangsrechnungen — bestehende Liste aus BuchhaltungPage wiederverwendet */}
      <div>
        <BuchhaltungPage embedded disableErstellung lockBusinessUnit={betrieb.businessUnit} />
      </div>

      {/* SV Büro: zusätzlich WinValue-Rechnungen */}
      {betrieb.withWinValue && (
        <div>
          <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-gray-400" /> WinValue-Rechnungen
          </h3>
          <WinValueRechnungenListe />
        </div>
      )}
    </div>
  );
}

// ============================================================
// FRISTEN-BANNER (Feature finanzen-cockpit-v2 / P4)
// Berechnet aus stb_frist_tag die Fälligkeit der Übermittlung des Vormonats.
// ============================================================

function FristenBanner() {
  const [fristTag, setFristTag] = useState<number>(DEFAULT_FRIST_TAG);

  useEffect(() => {
    let aktiv = true;
    ladeAppSettings(['stb_frist_tag']).then(map => {
      if (!aktiv) return;
      const t = Number(map['stb_frist_tag']);
      if (!Number.isNaN(t) && t >= 1 && t <= 28) setFristTag(t);
    });
    return () => { aktiv = false; };
  }, []);

  const info = useMemo(() => {
    const heute = new Date();
    heute.setHours(0, 0, 0, 0);
    // Übermittlung des Vormonats ist am `fristTag` des aktuellen Monats fällig.
    const faellig = new Date(heute.getFullYear(), heute.getMonth(), fristTag);
    const vormonat = new Date(heute.getFullYear(), heute.getMonth() - 1, 1);
    const vormonatLabel = vormonat.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    const msPerTag = 1000 * 60 * 60 * 24;
    const diffTage = Math.round((faellig.getTime() - heute.getTime()) / msPerTag);
    return { faellig, vormonatLabel, diffTage };
  }, [fristTag]);

  const ueberfaellig = info.diffTage < 0;

  return (
    <div className={`rounded-xl p-3 mb-5 flex items-start gap-2.5 border ${
      ueberfaellig ? 'bg-red-50 border-red-100' : 'bg-amber-50 border-amber-100'
    }`}>
      <Clock className={`w-4 h-4 flex-shrink-0 mt-0.5 ${ueberfaellig ? 'text-red-600' : 'text-amber-600'}`} />
      <div className={`text-xs ${ueberfaellig ? 'text-red-700' : 'text-amber-700'}`}>
        {ueberfaellig ? (
          <>Die Übermittlung der Buchhaltung für <strong>{info.vormonatLabel}</strong> ist seit{' '}
            <strong>{Math.abs(info.diffTage)} Tag(en)</strong> überfällig (Frist war der {formatDate(localIsoDate(info.faellig))}).</>
        ) : info.diffTage === 0 ? (
          <>Heute ist die Übermittlung der Buchhaltung für <strong>{info.vormonatLabel}</strong> fällig.</>
        ) : (
          <>In <strong>{info.diffTage} Tag(en)</strong> ist die Übermittlung der Buchhaltung für{' '}
            <strong>{info.vormonatLabel}</strong> fällig (bis {formatDate(localIsoDate(info.faellig))}).</>
        )}
      </div>
    </div>
  );
}

// ============================================================
// STB-VERSAND-DIALOG (Feature finanzen-cockpit-v2 / P3)
// Übersicht „Was wird gesendet?" + Freigabe (uebermittelt setzen + E-Mail-Entwurf).
// ============================================================

function StbVersandDialog({
  open, onClose, zeitraum, firma, items, onDone,
}: {
  open: boolean;
  onClose: () => void;
  zeitraum: string;
  firma: string;
  items: Eingangsbeleg[];
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);

  const summe = useMemo(() => items.reduce((s, b) => s + (b.brutto_betrag || 0), 0), [items]);
  const firmaLabel = FIRMA_LABELS[firma] || firma;
  const monLabel = monatLabel(zeitraum);

  const handleFreigeben = async () => {
    if (items.length === 0) {
      toast.info('Keine Belege in diesem Monat');
      return;
    }
    setBusy(true);
    try {
      const heute = localIsoDate(new Date());

      // (a) Alle Belege des Monats als übermittelt markieren.
      for (const b of items) {
        await updateEingangsbeleg(b.id, {
          uebermittlung_status: 'uebermittelt' as UebermittlungStatus,
          uebermittelt_am: heute,
          uebermittlung_zeitraum: zeitraum,
        });
      }

      // (b) E-Mail-Entwurf an den Steuerberater anlegen (Versand erst nach Freigabe).
      const settings = await ladeAppSettings(['steuerberater_email', 'steuerberater_name']);
      const stbEmail = settings['steuerberater_email'] || DEFAULT_STB_EMAIL;
      const stbName = settings['steuerberater_name'] || '';

      const betreff = `Buchhaltung ${firmaLabel} ${monLabel}`;
      const zeilen = items.map(b => {
        const datum = formatDate(b.belegdatum || b.eingangsdatum);
        const absender = b.absender_firma || b.absender_name || b.beleg_nr || '—';
        const kategorie = b.kategorie ? ` [${b.kategorie}]` : '';
        return `- ${datum} · ${absender}${kategorie} · ${formatEuro(b.brutto_betrag)}`;
      }).join('\n');

      const bodyText =
        `${stbName ? `Hallo ${stbName},` : 'Guten Tag,'}\n\n` +
        `anbei die Buchhaltungsbelege für ${firmaLabel} — Zeitraum ${monLabel}.\n\n` +
        `Anzahl Belege: ${items.length}\n` +
        `Summe (brutto): ${formatEuro(summe)}\n\n` +
        `Belegliste:\n${zeilen}\n\n` +
        `Die zugehörigen Belege werden separat bereitgestellt.\n\n` +
        `Mit freundlichen Grüßen`;

      const { error: draftErr } = await supabase
        .from('email_drafts')
        .insert({
          recipient: stbEmail,
          subject: betreff,
          body_text: bodyText,
          status: 'ready_for_review',
          created_by_agent_id: 'finanzen-cockpit-stb',
          review_note: `Monatliche Buchhaltung ${firmaLabel} ${monLabel} — ${items.length} Belege, ${formatEuro(summe)}. Versand nach Freigabe.`,
        });

      if (draftErr) {
        // Belege sind bereits als übermittelt markiert; Entwurf konnte nicht
        // angelegt werden → klarer Hinweis, kein harter Abbruch.
        toast.error('Belege übermittelt, aber E-Mail-Entwurf fehlgeschlagen: ' + (draftErr.message || 'Unbekannt'));
      } else {
        toast.success(`Monat freigegeben — ${items.length} Belege übermittelt, E-Mail-Entwurf erstellt`);
      }

      onClose();
      onDone();
    } catch (err: any) {
      toast.error('Fehler: ' + (err.message || 'Unbekannt'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-2 sm:p-6">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold flex items-center gap-2">
              <Send className="w-4 h-4 text-[#C41E3A]" /> Monat an Steuerberater senden
            </h2>
            <p className="text-xs text-gray-400">{firmaLabel} · {monLabel}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/50 flex-shrink-0">
          <div className="text-sm font-semibold text-gray-700 mb-1">Was wird gesendet?</div>
          <div className="flex gap-6 text-xs text-gray-600">
            <div><span className="text-gray-400">Belege:</span> <span className="font-bold">{items.length}</span></div>
            <div><span className="text-gray-400">Summe (brutto):</span> <span className="font-bold">{formatEuro(summe)}</span></div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3">
          {items.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-400">Keine Belege in diesem Monat</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left">
                  <th className="py-2 text-xs font-medium text-gray-500">Datum</th>
                  <th className="py-2 text-xs font-medium text-gray-500">Absender</th>
                  <th className="py-2 text-xs font-medium text-gray-500">Kategorie</th>
                  <th className="py-2 text-xs font-medium text-gray-500 text-right">Brutto</th>
                </tr>
              </thead>
              <tbody>
                {items.map(b => (
                  <tr key={b.id} className="border-b border-gray-50">
                    <td className="py-2 text-xs text-gray-500 whitespace-nowrap">{formatDate(b.belegdatum || b.eingangsdatum)}</td>
                    <td className="py-2 text-gray-900 truncate max-w-[220px]">{b.absender_firma || b.absender_name || b.beleg_nr || '—'}</td>
                    <td className="py-2 text-xs text-gray-500">{b.kategorie || '—'}</td>
                    <td className="py-2 text-right font-semibold text-gray-900 whitespace-nowrap">{formatEuro(b.brutto_betrag)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 space-y-3">
          <div className="flex items-start gap-2 text-[11px] text-gray-500 bg-blue-50/60 border border-blue-100 rounded-lg p-2.5">
            <Mail className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
            Versand erfolgt nach deiner Freigabe per E-Mail (DATEV-Übertragung folgt später).
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-100 transition-colors">
              Abbrechen
            </button>
            <button
              data-testid="stb-freigeben"
              onClick={handleFreigeben}
              disabled={busy || items.length === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#C41E3A] text-white text-sm font-semibold hover:bg-[#A3172F] transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Freigeben & Entwurf erstellen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// TAB: BUCHHALTUNG (monatliche Übermittlung an Steuerberater)
// ============================================================

export function BuchhaltungMonatTab({ firma }: { firma?: FirmaId }) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [versandDialog, setVersandDialog] = useState<{ zeitraum: string; firma: string; items: Eingangsbeleg[] } | null>(null);
  const { data: belegeRaw, loading, refetch } = useEingangsbelege(firma);
  const belege = belegeRaw || [];

  const kpis = useMemo(() => {
    const offen = belege.filter(b => (b.uebermittlung_status ?? 'offen') === 'offen').length;
    const spaeter = belege.filter(b => b.uebermittlung_status === 'spaeter').length;
    const uebermittelt = belege.filter(b => b.uebermittlung_status === 'uebermittelt').length;
    const gebucht = belege.filter(b => b.gebucht_beim_stb === true).length;
    return { offen, spaeter, uebermittelt, gebucht };
  }, [belege]);

  const gruppen = useMemo(() => {
    const byMonat = new Map<string, Map<string, Eingangsbeleg[]>>();
    for (const b of belege) {
      const z = belegZeitraum(b);
      if (!byMonat.has(z)) byMonat.set(z, new Map());
      const byFirma = byMonat.get(z)!;
      const f = b.firma || 'gutachter_koop';
      if (!byFirma.has(f)) byFirma.set(f, []);
      byFirma.get(f)!.push(b);
    }
    const monate = Array.from(byMonat.keys()).sort((a, c) => {
      if (a === 'ohne-datum') return 1;
      if (c === 'ohne-datum') return -1;
      return c.localeCompare(a);
    });
    return monate.map(z => ({
      zeitraum: z,
      firmen: Array.from(byMonat.get(z)!.entries()).map(([f, items]) => ({
        firma: f,
        items,
        summe: items.reduce((s, it) => s + (it.brutto_betrag || 0), 0),
        offen: items.filter(it => (it.uebermittlung_status ?? 'offen') === 'offen').length,
      })),
    }));
  }, [belege]);

  const setBeleg = async (id: string, updates: Partial<Eingangsbeleg>, okMsg: string) => {
    setBusyId(id);
    try {
      await updateEingangsbeleg(id, updates);
      toast.success(okMsg);
      refetch();
    } catch (err: any) {
      toast.error('Fehler: ' + (err.message || 'Unbekannt'));
    } finally {
      setBusyId(null);
    }
  };

  const handleUebermitteln = (b: Eingangsbeleg) =>
    setBeleg(b.id, {
      uebermittlung_status: 'uebermittelt' as UebermittlungStatus,
      uebermittelt_am: localIsoDate(new Date()),
      uebermittlung_zeitraum: belegZeitraum(b),
    }, 'An Steuerberater übermittelt');

  const handleSpaeter = (b: Eingangsbeleg) =>
    setBeleg(b.id, { uebermittlung_status: 'spaeter' as UebermittlungStatus }, 'Auf „später“ gesetzt');

  const handleGebucht = (b: Eingangsbeleg) =>
    setBeleg(b.id, {
      gebucht_beim_stb: true,
      gebucht_am: localIsoDate(new Date()),
    }, 'Als gebucht markiert');

  if (loading) return <LoadingBox />;

  return (
    <div>
      {/* P4: Fristen-Erinnerung */}
      <FristenBanner />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <KpiBox label="Offen" value={kpis.offen} icon={Clock} color="#2563EB" />
        <KpiBox label="Später" value={kpis.spaeter} icon={CalendarRange} color="#D97706" />
        <KpiBox label="Übermittelt" value={kpis.uebermittelt} icon={Send} color="#059669" />
        <KpiBox label="Beim StB gebucht" value={kpis.gebucht} icon={Banknote} color="#7C3AED" />
      </div>

      {gruppen.length === 0 ? (
        <EmptyBox icon={Inbox} text="Keine Belege vorhanden" />
      ) : (
        <div className="space-y-6">
          {gruppen.map(g => (
            <div key={g.zeitraum} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/60">
                <div className="flex items-center gap-2">
                  <CalendarRange className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-bold text-gray-900">{monatLabel(g.zeitraum)}</span>
                </div>
              </div>

              {g.firmen.map(fg => (
                <div key={fg.firma} className="border-b border-gray-50 last:border-b-0">
                  <div className="flex items-center justify-between px-4 py-2.5 bg-white">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-600">{FIRMA_LABELS[fg.firma] || fg.firma}</span>
                      <span className="text-[10px] text-gray-400">
                        {fg.items.length} Belege · {formatEuro(fg.summe)}
                        {fg.offen > 0 && <span className="text-blue-600 font-medium"> · {fg.offen} offen</span>}
                      </span>
                    </div>
                    <button
                      data-testid={`gruppe-stb-${g.zeitraum}-${fg.firma}`}
                      onClick={() => setVersandDialog({ zeitraum: g.zeitraum, firma: fg.firma, items: fg.items })}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-[#C41E3A] hover:bg-[#A3172F] rounded-lg transition-colors"
                    >
                      <Send className="w-3.5 h-3.5" />
                      Monat an Steuerberater
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <tbody>
                        {fg.items.map(b => {
                          const status = (b.uebermittlung_status ?? 'offen') as UebermittlungStatus;
                          const busy = busyId === b.id;
                          return (
                            <tr key={b.id} className="border-t border-gray-50 hover:bg-gray-50/50">
                              <td className="px-4 py-2.5 w-8">
                                <Receipt className="w-4 h-4 text-gray-400" />
                              </td>
                              <td className="px-2 py-2.5">
                                <div className="text-gray-900">{b.absender_firma || b.absender_name || b.beleg_nr || '—'}</div>
                                <div className="text-[10px] text-gray-400">{formatDate(b.belegdatum || b.eingangsdatum)}</div>
                              </td>
                              <td className="px-2 py-2.5 text-right whitespace-nowrap">
                                <span className="font-semibold text-gray-900">{formatEuro(b.brutto_betrag)}</span>
                              </td>
                              <td className="px-2 py-2.5">
                                <UebermittlungBadge status={status} gebucht={b.gebucht_beim_stb === true} />
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {status !== 'uebermittelt' && (
                                    <button
                                      data-testid={`beleg-uebermitteln-${b.id}`}
                                      onClick={() => handleUebermitteln(b)}
                                      disabled={busy}
                                      className="px-2 py-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 rounded hover:bg-emerald-100 transition-colors disabled:opacity-50"
                                    >
                                      Übermitteln
                                    </button>
                                  )}
                                  {status === 'offen' && (
                                    <button
                                      data-testid={`beleg-spaeter-${b.id}`}
                                      onClick={() => handleSpaeter(b)}
                                      disabled={busy}
                                      className="px-2 py-1 text-[10px] font-medium text-amber-600 bg-amber-50 rounded hover:bg-amber-100 transition-colors disabled:opacity-50"
                                    >
                                      Später
                                    </button>
                                  )}
                                  {b.gebucht_beim_stb !== true && (
                                    <button
                                      data-testid={`beleg-gebucht-${b.id}`}
                                      onClick={() => handleGebucht(b)}
                                      disabled={busy}
                                      className="px-2 py-1 text-[10px] font-medium text-purple-600 bg-purple-50 rounded hover:bg-purple-100 transition-colors disabled:opacity-50"
                                    >
                                      Gebucht
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <StbVersandDialog
        open={!!versandDialog}
        onClose={() => setVersandDialog(null)}
        zeitraum={versandDialog?.zeitraum || ''}
        firma={versandDialog?.firma || ''}
        items={versandDialog?.items || []}
        onDone={refetch}
      />
    </div>
  );
}

// ============================================================
// TAB: DOKUMENTE (wichtige inbox_items, die keine Belege sind)
// ============================================================

/** Klassifizierungen, die als „Beleg“ gelten und daher NICHT als Dokument zählen. */
const BELEG_KLASSEN: InboxKlassifizierung[] = [
  'eingangsrechnung',
  'versicherungs_abrechnung',
  'mahnung',
];

const DOK_KLASSE_LABELS: Partial<Record<InboxKlassifizierung, string>> = {
  versicherungs_anschreiben: 'Vers. Anschreiben',
  anwaltsschreiben: 'Anwaltsschreiben',
  gerichts_brief: 'Gerichtsbrief',
  schadensmeldung: 'Schadensmeldung',
  gutachten_anforderung: 'Gutachten-Anf.',
  reparatur_kostenvoranschlag: 'Kostenvoranschlag',
  zulassungsbescheinigung_zb1: 'ZB1',
  zulassungsbescheinigung_zb2: 'ZB2',
  kaufvertrag_fahrzeug: 'Kaufvertrag Kfz',
  leasingvertrag: 'Leasingvertrag',
  versicherungspolice: 'Versicherungspolice',
  mitarbeiter_dokument: 'Mitarbeiterdok.',
  sonstiges: 'Sonstiges',
};

export function DokumenteTab() {
  const { data: items, loading, error } = useInboxItems();

  const dokumente = useMemo(
    () => items.filter(i =>
      i.status !== 'abgelehnt' &&
      !(i.ki_klassifizierung && BELEG_KLASSEN.includes(i.ki_klassifizierung)),
    ),
    [items],
  );

  const handleOeffnen = async (item: InboxItem) => {
    try {
      const url = item.pdf_url || await getSignedInboxUrl(item.id);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Keine Datei hinterlegt');
      }
    } catch (e: any) {
      toast.error('Datei konnte nicht geöffnet werden: ' + (e.message || 'Unbekannt'));
    }
  };

  if (loading) return <LoadingBox />;

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-600 py-4">
        <AlertCircle className="w-4 h-4" /> {error}
      </div>
    );
  }

  return (
    <div>
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4 flex items-start gap-3">
        <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-700">
          Wichtige Dokumente (Verträge, Policen, Schreiben) aus dem Universal-Eingangskorb.
          Reine Buchhaltungs-Belege erscheinen unter „Eingangsrechnungen“.
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {dokumente.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <div className="text-sm">Keine Dokumente vorhanden</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-4 py-3 w-8" />
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Betreff / Absender</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Art</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Datum</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {dokumente.map(item => (
                  <tr key={item.id} data-testid={`dokument-${item.id}`} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 w-8">
                      {item.thumbnail_url ? (
                        <img src={item.thumbnail_url} alt="" className="w-7 h-7 rounded object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded bg-gray-100 flex items-center justify-center">
                          <FileText className="w-3.5 h-3.5 text-gray-400" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900 truncate max-w-[260px]">
                        {item.betreff || item.absender_name || item.absender_email || '—'}
                      </div>
                      {item.absender_email && item.betreff && (
                        <div className="text-[10px] text-gray-400 truncate">{item.absender_email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {item.ki_klassifizierung ? (
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-600">
                          {DOK_KLASSE_LABELS[item.ki_klassifizierung] || item.ki_klassifizierung}
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {formatDate(item.eingangs_datum)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`dokument-oeffnen-${item.id}`}
                        onClick={() => handleOeffnen(item)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-[#C41E3A] bg-red-50 rounded hover:bg-red-100 transition-colors"
                      >
                        <Eye className="w-3 h-3" /> Öffnen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// BETRIEB-PANEL (untere Tab-Ebene je Betrieb)
// ============================================================

function BetriebPanel({ betrieb }: { betrieb: BetriebConfig }) {
  return (
    <Tabs defaultValue="eingang">
      <TabsList className="mb-6 flex-wrap h-auto">
        <TabsTrigger value="eingang" data-testid={`tab-eingang-${betrieb.key}`}>
          <ArrowDownToLine className="w-4 h-4 mr-1.5" /> Eingangsrechnungen
        </TabsTrigger>
        <TabsTrigger value="ausgang" data-testid={`tab-ausgang-${betrieb.key}`}>
          <ArrowUpFromLine className="w-4 h-4 mr-1.5" /> Ausgangsrechnungen
        </TabsTrigger>
        <TabsTrigger value="dokumente" data-testid={`tab-dokumente-${betrieb.key}`}>
          <FileText className="w-4 h-4 mr-1.5" /> Dokumente
        </TabsTrigger>
        <TabsTrigger value="buchhaltung" data-testid={`tab-buchhaltung-${betrieb.key}`}>
          <CalendarRange className="w-4 h-4 mr-1.5" /> Buchhaltung
        </TabsTrigger>
      </TabsList>

      <TabsContent value="eingang">
        <EingangsrechnungenTab firma={betrieb.firma} />
      </TabsContent>
      <TabsContent value="ausgang">
        <AusgangsrechnungenTab betrieb={betrieb} />
      </TabsContent>
      <TabsContent value="dokumente">
        <DokumenteTab />
      </TabsContent>
      <TabsContent value="buchhaltung">
        <BuchhaltungMonatTab firma={betrieb.firma} />
      </TabsContent>
    </Tabs>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function BuchhaltungEinOrtPage() {
  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Buchhaltung</h1>
        <p className="text-sm text-gray-500 mt-1">
          Ein- und Ausgangsrechnungen, Dokumente und monatliche Buchhaltung — pro Betrieb an einem Ort
        </p>
      </div>

      <Tabs defaultValue="sv">
        <TabsList className="mb-6">
          {BETRIEBE_TABS.map(b => (
            <TabsTrigger key={b.key} value={b.key} data-testid={`betrieb-tab-${b.key}`}>
              <Building2 className="w-4 h-4 mr-1.5" /> {b.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {BETRIEBE_TABS.map(b => (
          <TabsContent key={b.key} value={b.key}>
            <BetriebPanel betrieb={b} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
